import type { WorkspaceSnapshot } from "@vekha/contracts";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  ApiRequestError,
  acknowledgePostBookingPreview,
  decideDraftRevision,
  describeApiError,
  discardPendingWorkspaceReads,
  editDraftRevision,
  fetchBootstrap,
  fetchWorkspace,
  subscribeDialogLive,
  WORKSPACE_DIALOGS_READ_LIMIT,
  WORKSPACE_RECOVERY_TIMEOUT_MS,
  type DialogLiveSignal,
} from "./api";
import { useAuth } from "./auth-context";
import type { AppContextValue, AppData, BootstrapStatus } from "./models";
import { pageSnapshotNeed, type PageSnapshotNeed } from "./page-snapshot";
import { createOpenReviewAttemptTracker } from "./open-review-attempt";
import { takeRead } from "./startup-reads";

const AppContext = createContext<AppContextValue | null>(null);

// Dialogs use an authenticated SSE stream. This is only a low-frequency
// recovery read for background state that may change outside the dialog UI.
export const WORKSPACE_POLL_INTERVAL_MS = 60_000;
// A workspace read is a multi-second rollup; live refreshes that it needs
// start at most this often.
export const WORKSPACE_LIVE_MIN_INTERVAL_MS = 2_000;
export const MANUAL_WORKSPACE_RECOVERY_ATTEMPTS = 2;

// The workspace carries drafts, escalations and the open review card. CRM and
// message changes elsewhere are patched by the dialog list itself.
export function workspaceNeedsLiveRefresh(
  signal: DialogLiveSignal,
  openConversationId: string | null,
): boolean {
  if (signal === null) return true;
  return signal.drafts
    || signal.escalations
    || (openConversationId !== null && signal.conversationIds.includes(openConversationId));
}

/** «Диалоги»: which of the chats on screen a live event changed — all of them after a reconnect or an escalation, none
 * when it names only other chats (a whole snapshot was read for every draft anywhere). */
export function dialogsLiveChange(
  signal: DialogLiveSignal,
  watched: ReadonlySet<string>,
  openConversationId: string | null,
): readonly string[] | "all" | null {
  if (signal === null || signal.escalations) return "all";
  const changed = signal.conversationIds.filter((id) => watched.has(id) || id === openConversationId);
  return changed.length > 0 ? changed : null;
}

function activationMilestoneRefreshKey(
  data: Pick<AppData, "activation" | "conversations">,
): string | null {
  if (data.activation.firstPreview !== null) return null;
  return data.conversations.find((conversation) =>
    conversation.operatorState.code === "review_ready"
    && conversation.draftRevisionId !== null
    && conversation.draft.length > 0)?.draftRevisionId ?? null;
}

export function activationMilestoneNeedsRefresh(
  data: Pick<AppData, "activation" | "conversations">,
): boolean {
  return activationMilestoneRefreshKey(data) !== null;
}

type WorkspaceVisibilityTarget = Readonly<{
  visibilityState: DocumentVisibilityState;
  addEventListener: (type: "visibilitychange", listener: EventListener) => void;
  removeEventListener: (type: "visibilitychange", listener: EventListener) => void;
}>;

type WorkspacePollingInput = Readonly<{
  visibilityTarget: WorkspaceVisibilityTarget;
  refresh: () => Promise<unknown>;
  intervalMs?: number;
}>;

type WorkspaceRefreshInput = Readonly<{
  read: () => Promise<WorkspaceSnapshot>;
  isCurrent?: () => boolean;
  confirm: (workspace: WorkspaceSnapshot) => void;
  clearError: () => void;
  reportError: (message: string) => void;
}>;

type WorkspaceLiveRefreshInput = Readonly<{
  subscribe: (onChanged: () => void) => () => void;
  refresh: () => Promise<unknown>;
  debounceMs?: number;
  // Minimum gap between refresh starts; changes inside it collapse into one.
  minIntervalMs?: number;
  // A hidden tab defers refreshes and catches up once when shown again.
  visibilityTarget?: WorkspaceVisibilityTarget;
}>;

export async function refreshWorkspaceSnapshot({
  read,
  isCurrent = () => true,
  confirm,
  clearError,
  reportError,
}: WorkspaceRefreshInput): Promise<boolean> {
  try {
    const workspace = await read();
    if (!isCurrent()) return false;
    confirm(workspace);
    clearError();
    return true;
  } catch (error) {
    if (!isCurrent()) return false;
    reportError(describeApiError(error));
    return false;
  }
}

export async function recoverWorkspaceSnapshot(
  refresh: () => Promise<boolean>,
  attempts = MANUAL_WORKSPACE_RECOVERY_ATTEMPTS,
): Promise<boolean> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (await refresh()) return true;
  }
  return false;
}

export function startWorkspaceLiveRefresh({
  subscribe,
  refresh,
  debounceMs = 240,
  minIntervalMs = 0,
  visibilityTarget,
}: WorkspaceLiveRefreshInput): () => void {
  let active = true;
  let inFlight = false;
  // A change arrived that no started refresh has read yet.
  let pending = false;
  let lastStartedAt = Number.NEGATIVE_INFINITY;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const hidden = () => visibilityTarget !== undefined && visibilityTarget.visibilityState !== "visible";
  const clearTimer = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  };

  const schedule = () => {
    if (!active || !pending || inFlight || hidden()) return;
    const now = Date.now();
    const startAt = Math.max(now + debounceMs, lastStartedAt + minIntervalMs);
    clearTimer();
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, startAt - now);
  };

  const run = async () => {
    if (!active || inFlight || hidden()) return;
    pending = false;
    inFlight = true;
    lastStartedAt = Date.now();
    try {
      await refresh();
    } catch {
      // The confirmed snapshot remains visible and the next live event retries.
    } finally {
      inFlight = false;
      schedule();
    }
  };

  const unsubscribe = subscribe(() => {
    if (!active) return;
    pending = true;
    schedule();
  });

  const handleVisibilityChange: EventListener = () => {
    if (hidden()) clearTimer();
    else schedule();
  };
  visibilityTarget?.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    active = false;
    pending = false;
    clearTimer();
    visibilityTarget?.removeEventListener("visibilitychange", handleVisibilityChange);
    unsubscribe();
  };
}

export function startWorkspacePolling({
  visibilityTarget,
  refresh,
  intervalMs = WORKSPACE_POLL_INTERVAL_MS,
}: WorkspacePollingInput): () => void {
  let active = true;
  let inFlight = false;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearScheduledPoll = () => {
    if (timer === null) return;
    clearTimeout(timer);
    timer = null;
  };

  const scheduleNextPoll = () => {
    if (!active || inFlight || timer !== null || visibilityTarget.visibilityState !== "visible") {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      void poll();
    }, intervalMs);
  };

  const poll = async () => {
    if (!active || inFlight || visibilityTarget.visibilityState !== "visible") {
      scheduleNextPoll();
      return;
    }
    inFlight = true;
    try {
      await refresh();
    } catch {
      // The provider refresh path records the visible error and polling resumes.
    } finally {
      inFlight = false;
      scheduleNextPoll();
    }
  };

  const handleVisibilityChange: EventListener = () => {
    if (visibilityTarget.visibilityState === "visible") scheduleNextPoll();
    else clearScheduledPoll();
  };

  visibilityTarget.addEventListener("visibilitychange", handleVisibilityChange);
  scheduleNextPoll();

  return () => {
    active = false;
    clearScheduledPoll();
    visibilityTarget.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}

const EMPTY_WORKSPACE: WorkspaceSnapshot = {
  conversations: [],
  reviewItems: [],
  employees: [],
  scenarios: [],
  workflowScenarios: [],
  modelRuntimes: [],
  knowledgeSources: [],
  mediaAssets: [],
  learningSignals: [],
  auditEvents: [],
  availableStages: [],
  availableChannelRoutes: [],
  scenarioActionCapabilities: [],
  scheduleWorkspace: {
    connectorAccounts: [],
    crmManagers: [],
    crmStages: [],
    bindings: [],
  },
};

const SAFE_UNAVAILABLE_DATA: AppData = {
  source: "database",
  actor: null,
  installation: {
    id: "00000000-0000-4000-8000-000000000000",
    name: "Рабочая установка недоступна",
  },
  activation: { firstPreview: null },
  delivery: {
    deliveryEnabled: false,
    autoDeliveryEnabled: false,
    mode: "preview",
    inFlightCount: 0,
    readiness: [],
    updatedAt: new Date(0).toISOString(),
  },
  connectors: [],
  attention: [],
  metrics: { waiting: 0, reviewReady: 0 },
  csrfToken: null,
  ...EMPTY_WORKSPACE,
};

function dataFromServer(
  bootstrap: Awaited<ReturnType<typeof fetchBootstrap>>,
  workspace: WorkspaceSnapshot,
  sessionCsrfToken: string | null,
): AppData {
  return {
    ...bootstrap.bootstrap,
    csrfToken: sessionCsrfToken ?? bootstrap.csrfToken,
    ...workspace,
  };
}

/** Every page but those that read the dialogs' snapshot opens on the bootstrap (page-snapshot.ts; 17.10.2026 «Аналитика»
 * and 18.10.2026 «Аналитика календаря» waited 3–4 s for 2 MB of dialogs). */
export const opensWithoutWorkspace = (pathname: string) => {
  const need = pageSnapshotNeed(pathname);
  return need === "none" || need === "context";
};

/** The bootstrap alone opens the page. The part of the snapshot the page reads behind it — the work context, not the whole
 * 2 MB that was read behind every page for nothing until 18.10.2026 — starts with the bootstrap and is merged by the caller
 * once the page's data are set; its failure is never the page's. */
export async function loadWithWorkspaceBehind(sessionCsrfToken: string | null, read: {
  bootstrap: () => ReturnType<typeof fetchBootstrap>; workspace?: () => Promise<WorkspaceSnapshot>;
}): Promise<{ data: AppData; behind: Promise<WorkspaceSnapshot> | null }> {
  const behind = read.workspace?.() ?? null;
  behind?.catch(() => undefined);
  const bootstrap = await read.bootstrap();
  return { data: dataFromServer(bootstrap, EMPTY_WORKSPACE, sessionCsrfToken), behind };
}

/** What a refresh of the workspace (a poll, a live event, «Перечитать») reads on a page: «Диалоги» their chats (and the
 * context), a page with the agent and scenario pickers the context alone (as the root and the old addresses of «Диалоги»
 * before they lead on), the pages of the whole snapshot the whole one, a page with a label in its top bar nothing (its
 * context was read once as it opened). Only «Диалоги» and the pages of the whole snapshot follow the live stream; what the
 * others do not watch is read again when a page that shows it is opened. */
export function workspaceReadOf(need: PageSnapshotNeed, dialogs: boolean): "dialogs" | "context" | "whole" | null {
  if (dialogs) return "dialogs";
  if (need === "whole") return "whole";
  return need === "none" ? null : "context";
}

/** A read older than a poll, or one made before its data stopped being watched (a page that does not show them was
 * opened since), is read again when its page is opened. */
export function workspaceReadStale(at: number | null, unwatchedAt: number, now: number): boolean {
  return at === null || now - at > WORKSPACE_POLL_INTERVAL_MS || unwatchedAt > at;
}

/** «Диалоги» read their part of the snapshot (fetchWorkspace's dialogs read): the chats on screen, not the 200 newest
 * (17.10.2026: the page waited 2–4 s for 2 MB it did not show, the open deal for two more snapshots of 2 MB). */
const DIALOGS = /^\/dialogs\/*$/u;
export const readsDialogsPart = (pathname: string) => DIALOGS.test(pathname);
/** The pages that show the whole snapshot (agents, scenarios, …): after «Диалоги» they wait for it (page-snapshot.ts). */
export const needsWholeWorkspace = (pathname: string) => pageSnapshotNeed(pathname) === "whole";

/** The dialogs' part of these chats, in reads of at most 100; the open card's chat is the review of the first one. */
export async function readDialogsPart(
  conversationIds: readonly string[],
  reviewConversationId?: string,
  reviewPreviewFenceId?: string,
  timeoutMs?: number,
): Promise<WorkspaceSnapshot> {
  const chats = [...new Set(conversationIds)].filter((id) => id !== reviewConversationId);
  const chunks: string[][] = [];
  for (let start = 0; start === 0 || start < chats.length; start += WORKSPACE_DIALOGS_READ_LIMIT) {
    chunks.push(chats.slice(start, start + WORKSPACE_DIALOGS_READ_LIMIT));
  }
  const parts = await Promise.all(chunks.map((chunk, index) => fetchWorkspace(
    index === 0 ? reviewConversationId : undefined,
    index === 0 ? reviewPreviewFenceId : undefined,
    timeoutMs,
    { conversationIds: chunk },
  )));
  return { ...parts[0]!, conversations: parts.flatMap((part) => part.conversations), reviewItems: parts.flatMap((part) => part.reviewItems) };
}

/** The dialogs' part merged into the data: the asked chats are replaced by the answer — a chat out of Yana's work is absent
 * from it, as from the whole snapshot — the others stay; the employees, scenarios, stages and media come whole. */
export function mergeDialogsPart(current: AppData, part: WorkspaceSnapshot, asked: readonly string[]): AppData {
  const askedIds = new Set(asked);
  const answered = new Map(part.conversations.filter((conversation) => askedIds.has(conversation.id)).map((conversation) => [conversation.id, conversation]));
  const present = new Set(current.conversations.map((conversation) => conversation.id));
  return {
    ...current,
    conversations: [
      ...current.conversations.flatMap((conversation) => {
        if (!askedIds.has(conversation.id)) return [conversation];
        const fresh = answered.get(conversation.id);
        return fresh ? [fresh] : [];
      }),
      ...[...answered.values()].filter((conversation) => !present.has(conversation.id)),
    ],
    reviewItems: [
      ...current.reviewItems.filter((item) => !askedIds.has(item.conversationId)),
      ...part.reviewItems.filter((item) => askedIds.has(item.conversationId)),
    ],
    employees: part.employees,
    workflowScenarios: part.workflowScenarios,
    availableStages: part.availableStages,
    mediaAssets: part.mediaAssets,
  };
}

export async function loadApplicationData(
  sessionCsrfToken: string | null,
  workspaceEnabled = true,
  dialogsChats: readonly string[] | null = null,
): Promise<AppData> {
  // The operator's bootstrap and «Диалоги»' part may already be on their way (main.tsx starts them with the page);
  // the calendar's bootstrap (scope=calendar) is a read of its own.
  const scope = workspaceEnabled ? undefined : "calendar";
  const workspace = !workspaceEnabled ? Promise.resolve(EMPTY_WORKSPACE)
    : dialogsChats === null ? takeRead("workspace", () => fetchWorkspace())
      : dialogsChats.length === 0 ? takeRead("dialogs-workspace", () => readDialogsPart([]))
        : readDialogsPart(dialogsChats);
  const [bootstrap, snapshot] = await Promise.all([workspaceEnabled ? takeRead("bootstrap", () => fetchBootstrap(scope)) : takeRead("calendar-scope-bootstrap", () => fetchBootstrap(scope)), workspace]);
  return dataFromServer(bootstrap, snapshot, sessionCsrfToken);
}

export function AppProvider({ children, liveDialogs = true, workspaceEnabled = true, pathname }: { children: ReactNode; liveDialogs?: boolean; workspaceEnabled?: boolean; pathname?: string }) {
  const { session } = useAuth();
  const currentPath = pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  // «Диалоги» read their part of the snapshot: the chats they show (rows and the open deal), see readsDialogsPart.
  const dialogsPart = workspaceEnabled && readsDialogsPart(currentPath);
  const dialogsPartRef = useRef(dialogsPart);
  dialogsPartRef.current = dialogsPart;
  // What the page needs of the snapshot (page-snapshot.ts): a page with a label in its top bar only the work context, once.
  const need: PageSnapshotNeed = workspaceEnabled ? pageSnapshotNeed(currentPath) : "none";
  const needRef = useRef(need);
  needRef.current = need;
  // The work context (employees, scenarios, stages, media) has come — with the whole snapshot or a part of it.
  const [contextReady, setContextReady] = useState(false);
  // When each read last started that was confirmed, and since when the chats and the whole snapshot are not watched (a page
  // that does not show them is open: no live stream, no poll of them): a page opened later reads what is older than a poll
  // or was not watched (workspaceReadStale).
  const readAtRef = useRef<{ whole: number | null; context: number | null; dialogs: number | null }>({ whole: null, context: null, dialogs: null });
  const unwatchedAtRef = useRef(Number.NEGATIVE_INFINITY);
  // The work context read behind a page that opened without the snapshot, started with the bootstrap.
  const behindRef = useRef<Promise<unknown> | null>(null);
  const dialogsWatchRef = useRef<ReadonlySet<string>>(new Set());
  const dialogsLoadedRef = useRef<ReadonlySet<string>>(new Set());
  const [dialogsLoadedIds, setDialogsLoadedIds] = useState<ReadonlySet<string>>(() => new Set());
  const dialogsLoadingRef = useRef(new Set<string>());
  // For each chat, the read started last: an earlier read that answers later does not overwrite it (an open card's focused
  // read carries the booking preview's fence, a background read of the rows does not).
  const dialogsReadSeqRef = useRef(0);
  const dialogsReadStartRef = useRef(new Map<string, number>());
  // Chats named by live events since the last live read («all» after a reconnect or an escalation).
  const liveDialogsRef = useRef<Set<string> | "all">(new Set());
  const [wholeWorkspaceReady, setWholeWorkspaceReady] = useState(false);
  const sessionKey = session
    ? `${session.actor.organizationId}:${session.actor.userId}:${session.csrfToken}`
    : null;
  const [data, setData] = useState<AppData>(SAFE_UNAVAILABLE_DATA);
  const [status, setStatus] = useState<BootstrapStatus>("loading");
  const [retryToken, setRetryToken] = useState(0);
  const [bootstrapError, setBootstrapError] = useState<string | null>(null);
  const [workspaceError, setWorkspaceError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const sessionKeyRef = useRef(sessionKey);
  const workspaceRefreshRef = useRef<Promise<boolean> | null>(null);
  // «Диалоги»: the open card read in flight — a second request for the same card joins it.
  const workspaceRefreshCardRef = useRef<string | null>(null);
  const applicationRefreshRef = useRef<Promise<boolean> | null>(null);
  const activationRefreshAttemptRef = useRef<string | null>(null);
  const openReviewAttemptRef = useRef(createOpenReviewAttemptTracker());
  // The review card that is on screen. Unlike the tracker, a plain workspace
  // refresh does not forget it, so background reads keep that conversation.
  const openReviewCardRef = useRef<string | null>(null);
  sessionKeyRef.current = sessionKey;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setDialogsLoaded = useCallback((next: ReadonlySet<string>) => {
    dialogsLoadedRef.current = next;
    setDialogsLoadedIds(next);
  }, []);
  const markDialogsLoaded = useCallback((conversationIds: readonly string[]) => {
    setDialogsLoaded(new Set([...dialogsLoadedRef.current, ...conversationIds]));
  }, [setDialogsLoaded]);
  const beginDialogsRead = useCallback((conversationIds: readonly string[]) => {
    const seq = ++dialogsReadSeqRef.current;
    conversationIds.forEach((id) => {
      dialogsReadStartRef.current.set(id, seq);
      dialogsLoadingRef.current.add(id);
    });
    return seq;
  }, []);
  /** The asked chats no later read has started for: only they are merged; the others are released by that later read. */
  const finishDialogsRead = useCallback((conversationIds: readonly string[], seq: number) => {
    const latest = conversationIds.filter((id) => dialogsReadStartRef.current.get(id) === seq);
    latest.forEach((id) => dialogsLoadingRef.current.delete(id));
    return latest;
  }, []);

  useEffect(() => {
    activationRefreshAttemptRef.current = null;
    openReviewAttemptRef.current.clear();
    openReviewCardRef.current = null;
    discardPendingWorkspaceReads();
    dialogsWatchRef.current = new Set();
    dialogsLoadingRef.current.clear();
    dialogsReadStartRef.current.clear();
    setDialogsLoaded(new Set());
  }, [sessionKey, setDialogsLoaded]);

  useEffect(() => {
    let active = true;
    setStatus("loading");
    setBootstrapError(null);
    setWorkspaceError(null);
    const opened = workspaceEnabled ? pageSnapshotNeed(typeof window === "undefined" ? "/" : window.location.pathname) : "whole";
    const started = Date.now();
    const loaded = opened === "none" || opened === "context"
      // The work context (agents, scenarios: ~160 KB, ~50 ms) behind the page, not the whole snapshot: the pickers of the
      // top bar show it, and on a page with a label the menu's links carry it and «Диалоги» open at once from there.
      ? loadWithWorkspaceBehind(session?.csrfToken ?? null, {
        bootstrap: () => takeRead("bootstrap", () => fetchBootstrap()),
        workspace: () => readDialogsPart([]),
      })
      // «Диалоги» open on their part without chats; the page asks for the chats it shows.
      : loadApplicationData(session?.csrfToken ?? null, workspaceEnabled, opened === "dialogs-part" ? [] : null)
        .then((nextData) => ({ data: nextData, behind: null }));
    void loaded.then(({ data: nextData, behind }) => {
      if (!active) return;
      setData(nextData);
      const whole = opened === "whole";
      const context = whole || opened === "dialogs-part";
      readAtRef.current = { whole: whole ? started : null, context: context ? started : null, dialogs: opened === "dialogs-part" ? started : null };
      setWholeWorkspaceReady(whole);
      setContextReady(context);
      behindRef.current = behind;
      behind?.then((part) => {
        if (!active) return;
        setData((current) => mergeDialogsPart(current, part, []));
        readAtRef.current.context = started;
        setContextReady(true);
      }, (error: unknown) => {
        // A page with a label does not show the context: a page that does reads it again when it is opened.
        if (active && opened === "context") setWorkspaceError(describeApiError(error));
      }).finally(() => {
        if (behindRef.current === behind) behindRef.current = null;
      });
      setStatus("ready");
    }).catch((error: unknown) => {
      if (!active) return;
      setBootstrapError(describeApiError(error));
      setStatus("unavailable");
    });
    return () => {
      active = false;
    };
  }, [retryToken, session?.csrfToken, setDialogsLoaded, workspaceEnabled]);

  const retryApplication = useCallback(() => {
    setRetryToken((current) => current + 1);
  }, []);

  const startOrJoinWorkspaceRefresh = useCallback((
    reviewConversationId?: string,
    reviewPreviewFenceId?: string,
    timeoutMs?: number,
    // «Диалоги»: the chats to read (by default all on screen); the open card's chat is read with them.
    dialogsChats?: readonly string[],
  ): Promise<boolean> => {
    if (!workspaceEnabled) return Promise.resolve(true);
    const kind = workspaceReadOf(needRef.current, dialogsPartRef.current);
    if (kind === null) return Promise.resolve(true);
    if (applicationRefreshRef.current) return applicationRefreshRef.current;
    if (workspaceRefreshRef.current) return workspaceRefreshRef.current;

    const requestedSessionKey = sessionKey;
    const started = Date.now();
    // A page with the pickers reads their context alone, no chat and no open card.
    const review = kind === "context" ? undefined : reviewConversationId;
    const fence = kind === "context" ? undefined : reviewPreviewFenceId;
    const reusePreviewFenceId = fence
      ?? openReviewAttemptRef.current.reuseFor(review);
    const asked = kind === "dialogs"
      ? [...new Set([...(dialogsChats ?? dialogsWatchRef.current), ...(review ? [review] : [])])]
      : kind === "context" ? [] : null;
    workspaceRefreshCardRef.current = asked && dialogsChats?.length === 0 && review
      ? `${review}:${fence ?? ""}`
      : null;
    const seq = asked ? beginDialogsRead(asked) : 0;
    let merged: readonly string[] = [];
    let trackedPromise: Promise<boolean>;
    trackedPromise = refreshWorkspaceSnapshot({
      read: asked
        ? async () => {
          const part = await readDialogsPart(asked, review, reusePreviewFenceId, timeoutMs);
          merged = finishDialogsRead(asked, seq);
          return part;
        }
        : () => fetchWorkspace(review, reusePreviewFenceId, timeoutMs),
      isCurrent: () => mountedRef.current && sessionKeyRef.current === requestedSessionKey,
      confirm: (workspace) => setData((current) => {
        const next = asked ? mergeDialogsPart(current, workspace, merged) : { ...current, ...workspace };
        if (review) {
          const reviewed = next.conversations.find((conversation) => conversation.id === review);
          openReviewAttemptRef.current.confirm(
            review,
            reviewed?.postBookingPreviewFenceId,
          );
        } else {
          openReviewAttemptRef.current.clear();
        }
        return next;
      }),
      clearError: () => setWorkspaceError(null),
      reportError: setWorkspaceError,
    }).then((confirmed) => {
      if (!confirmed && asked) finishDialogsRead(asked, seq);
      if (confirmed && asked) markDialogsLoaded(merged);
      if (confirmed) {
        readAtRef.current.context = started;
        setContextReady(true);
      }
      if (confirmed && kind === "dialogs" && dialogsChats === undefined) readAtRef.current.dialogs = started;
      if (confirmed && !asked) {
        // The whole snapshot holds the 200 newest dialogs: «Диалоги» read their chats again when they open.
        readAtRef.current.whole = started;
        setWholeWorkspaceReady(true);
        setDialogsLoaded(new Set());
      }
      return confirmed;
    }).finally(() => {
      if (workspaceRefreshRef.current !== trackedPromise) return;
      workspaceRefreshRef.current = null;
      workspaceRefreshCardRef.current = null;
    });
    workspaceRefreshRef.current = trackedPromise;
    return trackedPromise;
  }, [beginDialogsRead, finishDialogsRead, markDialogsLoaded, sessionKey, setDialogsLoaded, workspaceEnabled]);

  /** «Диалоги» name the chats on screen; the ones not read yet are read now (the rest stay as they are). */
  const watchDialogs = useCallback((conversationIds: readonly string[]) => {
    dialogsWatchRef.current = new Set(conversationIds);
    if (!dialogsPartRef.current) return;
    // A chat being read already (an open card's focused read among them) is not asked again.
    const missing = conversationIds.filter((id) => !dialogsLoadedRef.current.has(id) && !dialogsLoadingRef.current.has(id));
    if (missing.length === 0) return;
    const seq = beginDialogsRead(missing);
    const requestedSessionKey = sessionKeyRef.current;
    const isCurrent = () => mountedRef.current && sessionKeyRef.current === requestedSessionKey;
    void readDialogsPart(missing).then((part) => {
      const latest = finishDialogsRead(missing, seq);
      if (!isCurrent()) return;
      setData((current) => mergeDialogsPart(current, part, latest));
      markDialogsLoaded(latest);
      setWorkspaceError(null);
    }, (error: unknown) => {
      finishDialogsRead(missing, seq);
      if (isCurrent()) setWorkspaceError(describeApiError(error));
    });
  }, [beginDialogsRead, finishDialogsRead, markDialogsLoaded]);

  const dialogsLoaded = useCallback(
    (conversationIds: readonly string[]) => conversationIds.every((id) => dialogsLoadedIds.has(id)),
    [dialogsLoadedIds],
  );

  // A page that shows the whole snapshot waits for it, «Диалоги» for their context (workspaceReadyFor) — opened after a
  // page that read less of the snapshot; a page with the pickers fills them in when the context comes.
  const workspaceReadyFor = useCallback((path: string) => {
    if (!workspaceEnabled) return true;
    const pathNeed = pageSnapshotNeed(path);
    return pathNeed === "whole" ? wholeWorkspaceReady : pathNeed === "dialogs-part" ? contextReady : true;
  }, [contextReady, wholeWorkspaceReady, workspaceEnabled]);

  const refreshWorkspace = useCallback(async (
    reviewConversationId?: string,
    reviewPreviewFenceId?: string,
  ): Promise<boolean> => {
    const currentRefresh = applicationRefreshRef.current ?? workspaceRefreshRef.current;
    // «Диалоги»: the open card is read alone; the page and the card asking for it together make one read.
    const card = dialogsPartRef.current && reviewConversationId ? `${reviewConversationId}:${reviewPreviewFenceId ?? ""}` : null;
    if (currentRefresh && card !== null && workspaceRefreshCardRef.current === card) return currentRefresh;
    if (currentRefresh) {
      await currentRefresh;
      if (!mountedRef.current || sessionKeyRef.current !== sessionKey) return false;
    }
    return startOrJoinWorkspaceRefresh(
      reviewConversationId,
      reviewPreviewFenceId,
      undefined,
      card !== null ? [] : undefined,
    );
  }, [sessionKey, startOrJoinWorkspaceRefresh]);

  const recoverWorkspace = useCallback(async (): Promise<boolean> => {
    const currentRefresh = applicationRefreshRef.current ?? workspaceRefreshRef.current;
    if (currentRefresh) {
      await currentRefresh;
      if (!mountedRef.current || sessionKeyRef.current !== sessionKey) return false;
    }
    const openAttempt = openReviewAttemptRef.current.get();
    return startOrJoinWorkspaceRefresh(
      openAttempt?.conversationId,
      openAttempt?.fenceId,
      WORKSPACE_RECOVERY_TIMEOUT_MS,
    );
  }, [sessionKey, startOrJoinWorkspaceRefresh]);

  const beginOpenReviewAttempt = useCallback((conversationId: string) => {
    openReviewCardRef.current = conversationId;
    openReviewAttemptRef.current.begin(conversationId);
  }, []);

  const endOpenReviewAttempt = useCallback((conversationId?: string) => {
    if (conversationId === undefined || openReviewCardRef.current === conversationId) {
      openReviewCardRef.current = null;
    }
    openReviewAttemptRef.current.end(conversationId);
  }, []);

  const acknowledgeVisiblePostBookingPreview = useCallback(async (
    conversationId: string,
    input: { fenceId: string },
  ): Promise<boolean> => {
    const requestedSessionKey = sessionKey;
    try {
      await acknowledgePostBookingPreview(conversationId, input, session?.csrfToken ?? null);
      if (!mountedRef.current || sessionKeyRef.current !== requestedSessionKey) return false;
      setData((current) => ({
        ...current,
        conversations: current.conversations.map((conversation) => (
          conversation.id === conversationId && conversation.postBookingPreviewFenceId === input.fenceId
            ? { ...conversation, postBookingPreviewFenceReady: true }
            : conversation
        )),
      }));
      return true;
    } catch (error) {
      if (!mountedRef.current || sessionKeyRef.current !== requestedSessionKey) return false;
      setWorkspaceError(describeApiError(error));
      return false;
    }
  }, [session?.csrfToken, sessionKey]);

  const refreshOpenWorkspace = useCallback((dialogsChats?: readonly string[]) => {
    const openAttempt = openReviewAttemptRef.current.get();
    return startOrJoinWorkspaceRefresh(
      openReviewCardRef.current ?? openAttempt?.conversationId,
      openAttempt?.fenceId,
      undefined,
      dialogsChats,
    );
  }, [startOrJoinWorkspaceRefresh]);

  // A page opened after one that read less of the snapshot, or long ago: what it shows is read now — the whole snapshot,
  // the context (before «Диалоги» show, behind a page with the pickers), and the chats of «Диалоги» again (watchDialogs).
  const readKind = workspaceEnabled && status === "ready" ? workspaceReadOf(need, dialogsPart) : null;
  // The live stream and the polls of the chats and of the whole snapshot run on the pages that show them.
  const watchesLive = readKind === "dialogs" || readKind === "whole";
  useEffect(() => {
    const now = Date.now();
    if (status === "ready" && !watchesLive) unwatchedAtRef.current = now;
    if (readKind === null || behindRef.current) return;
    const reads = readAtRef.current;
    const stale = (at: number | null) => workspaceReadStale(at, unwatchedAtRef.current, now);
    if (readKind === "whole") {
      if (!wholeWorkspaceReady || stale(reads.whole)) void refreshWorkspace();
      return;
    }
    if (readKind === "dialogs" && stale(reads.dialogs)) {
      readAtRef.current.dialogs = now;
      setDialogsLoaded(new Set());
    }
    // The context changes with the operators' switches only: a poll's age is enough for it.
    if (!contextReady || workspaceReadStale(reads.context, Number.NEGATIVE_INFINITY, now)) {
      void startOrJoinWorkspaceRefresh(undefined, undefined, undefined, []);
    }
  }, [contextReady, readKind, refreshWorkspace, setDialogsLoaded, startOrJoinWorkspaceRefresh, status, watchesLive, wholeWorkspaceReady]);

  useEffect(() => {
    if (!workspaceEnabled || status !== "ready" || !sessionKey || typeof document === "undefined") return undefined;
    return startWorkspacePolling({
      visibilityTarget: document,
      refresh: () => refreshOpenWorkspace(),
    });
  }, [refreshOpenWorkspace, sessionKey, status, workspaceEnabled]);

  useEffect(() => {
    // The semantic sandbox has its own polling and deliberately denies live CRM routes.
    if (!liveDialogs || !watchesLive || status !== "ready" || !sessionKey || typeof EventSource === "undefined") return undefined;
    // The only live workspace subscription: an open review card is read
    // through refreshOpenWorkspace instead of a second stream of its own.
    return startWorkspaceLiveRefresh({
      subscribe: (notify) => subscribeDialogLive((signal) => {
        if (!dialogsPartRef.current) {
          if (workspaceNeedsLiveRefresh(signal, openReviewCardRef.current)) notify();
          return;
        }
        // «Диалоги»: only the changed chats that are on screen (all of them after a reconnect or an escalation).
        const changed = dialogsLiveChange(signal, dialogsWatchRef.current, openReviewCardRef.current);
        if (changed === null) return;
        const pending = liveDialogsRef.current;
        liveDialogsRef.current = changed === "all" || pending === "all" ? "all" : new Set([...pending, ...changed]);
        notify();
      }),
      refresh: () => {
        const changed = liveDialogsRef.current;
        liveDialogsRef.current = new Set();
        return refreshOpenWorkspace(changed === "all" || !dialogsPartRef.current ? undefined : [...changed]);
      },
      minIntervalMs: WORKSPACE_LIVE_MIN_INTERVAL_MS,
      visibilityTarget: document,
    });
  }, [liveDialogs, refreshOpenWorkspace, sessionKey, status, watchesLive]);

  const startOrJoinApplicationRefresh = useCallback((): Promise<boolean> => {
    if (applicationRefreshRef.current) return applicationRefreshRef.current;
    if (workspaceRefreshRef.current) {
      return workspaceRefreshRef.current.then(() => startOrJoinApplicationRefresh());
    }

    const requestedSessionKey = sessionKey;
    const kind = workspaceEnabled ? workspaceReadOf(needRef.current, dialogsPartRef.current) : "whole";
    const dialogsChats = kind === "dialogs" ? [...dialogsWatchRef.current] : null;
    const started = Date.now();
    const csrfToken = session?.csrfToken ?? null;
    // A page without the whole snapshot or the dialogs reads the bootstrap (and its context) into what is already there:
    // the snapshot read on another page stays for it.
    const read: Promise<(current: AppData) => AppData> = kind === null || kind === "context"
      ? Promise.all([fetchBootstrap(), kind === "context" ? readDialogsPart([]) : null]).then(([bootstrap, part]) => (current: AppData) => {
        const next = { ...current, ...bootstrap.bootstrap, csrfToken: csrfToken ?? bootstrap.csrfToken };
        return part ? mergeDialogsPart(next, part, []) : next;
      })
      : loadApplicationData(csrfToken, workspaceEnabled, dialogsChats).then((nextData) => () => nextData);
    let trackedPromise: Promise<boolean>;
    trackedPromise = read.then((update) => {
      if (!mountedRef.current || sessionKeyRef.current !== requestedSessionKey) return false;
      setData(update);
      if (kind !== null) {
        readAtRef.current.context = started;
        setContextReady(true);
      }
      if (dialogsChats) {
        readAtRef.current.dialogs = started;
        setDialogsLoaded(new Set(dialogsChats));
      } else if (kind === "whole") {
        readAtRef.current.whole = started;
        setWholeWorkspaceReady(true);
        setDialogsLoaded(new Set());
      }
      setBootstrapError(null);
      setWorkspaceError(null);
      return true;
    }).catch((error: unknown) => {
      if (!mountedRef.current || sessionKeyRef.current !== requestedSessionKey) return false;
      setBootstrapError(describeApiError(error));
      return false;
    }).finally(() => {
      if (applicationRefreshRef.current === trackedPromise) applicationRefreshRef.current = null;
    });
    applicationRefreshRef.current = trackedPromise;
    return trackedPromise;
  }, [session?.csrfToken, sessionKey, setDialogsLoaded, workspaceEnabled]);

  const refreshApplication = useCallback(async (): Promise<boolean> => {
    let currentRefresh = applicationRefreshRef.current ?? workspaceRefreshRef.current;
    while (currentRefresh) {
      await currentRefresh;
      if (!mountedRef.current || sessionKeyRef.current !== sessionKey) return false;
      currentRefresh = applicationRefreshRef.current ?? workspaceRefreshRef.current;
    }
    return startOrJoinApplicationRefresh();
  }, [sessionKey, startOrJoinApplicationRefresh]);

  useEffect(() => {
    if (status !== "ready" || !sessionKey) return;
    const refreshKey = activationMilestoneRefreshKey(data);
    if (refreshKey === null || activationRefreshAttemptRef.current === refreshKey) return;
    activationRefreshAttemptRef.current = refreshKey;
    void refreshApplication().then((refreshed) => {
      if (!refreshed && activationRefreshAttemptRef.current === refreshKey) {
        activationRefreshAttemptRef.current = null;
      }
    });
  }, [data, refreshApplication, sessionKey, status]);

  const requireFreshServerState = useCallback(() => {
    if (bootstrapError || workspaceError) {
      throw new ApiRequestError(
        "Сначала перечитайте подтверждённое состояние сервера.",
        { code: "STATE_REFRESH_REQUIRED" },
      );
    }
  }, [bootstrapError, workspaceError]);

  const saveDraftRevision = useCallback<AppContextValue["saveDraftRevision"]>(
    async (draftRevisionId, input) => {
      requireFreshServerState();
      const result = await editDraftRevision(draftRevisionId, input, data.csrfToken);
      return { result, workspaceRefreshed: await refreshWorkspace() };
    },
    [data.csrfToken, refreshWorkspace, requireFreshServerState],
  );

  const decideDraft = useCallback<AppContextValue["decideDraft"]>(
    async (draftRevisionId, input) => {
      requireFreshServerState();
      const result = await decideDraftRevision(draftRevisionId, input, data.csrfToken);
      return { result, workspaceRefreshed: await refreshWorkspace() };
    },
    [data.csrfToken, refreshWorkspace, requireFreshServerState],
  );

  const value = useMemo<AppContextValue>(() => ({
    data,
    status,
    bootstrapError,
    workspaceError,
    retryApplication,
    beginOpenReviewAttempt,
    endOpenReviewAttempt,
    refreshWorkspace,
    recoverWorkspace,
    acknowledgePostBookingPreview: acknowledgeVisiblePostBookingPreview,
    refreshApplication,
    saveDraftRevision,
    decideDraft,
    watchDialogs,
    dialogsLoaded,
    workspaceReadyFor,
  }), [
    acknowledgeVisiblePostBookingPreview,
    beginOpenReviewAttempt,
    bootstrapError,
    data,
    decideDraft,
    dialogsLoaded,
    watchDialogs,
    workspaceReadyFor,
    endOpenReviewAttempt,
    refreshApplication,
    refreshWorkspace,
    recoverWorkspace,
    retryApplication,
    saveDraftRevision,
    status,
    workspaceError,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp(): AppContextValue {
  const value = useContext(AppContext);
  if (!value) throw new Error("useApp must be used inside AppProvider");
  return value;
}
